import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { useAuth } from "../hooks/useAuth"
import {
  useAnnouncements,
  formatAnnouncementDate,
  dateInputToIso,
  isoToDateInput,
  type Announcement,
  type AnnouncementInput,
} from "../hooks/useAnnouncements"

interface FormState {
  title: string
  content: string
  active: boolean
  publishedAt: string
  bumpRevision: boolean
}

const EMPTY_FORM: FormState = {
  title: "",
  content: "",
  active: true,
  publishedAt: new Date().toISOString().slice(0, 10),
  bumpRevision: false,
}

/** Admin 전용 공지사항 관리 화면 */
export function AnnouncementManagement() {
  const auth = useAuth()
  const {
    announcements,
    loading,
    fetchAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    toggleActive,
    deleteAnnouncement,
  } = useAnnouncements()

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Announcement | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null)

  useEffect(() => {
    fetchAnnouncements()
  }, [fetchAnnouncements])

  const activeAnnouncement = announcements.find((a) => a.active) || null

  const openCreate = () => {
    setEditing(null)
    setForm({ ...EMPTY_FORM, publishedAt: new Date().toISOString().slice(0, 10) })
    setModalOpen(true)
  }

  const openEdit = (target: Announcement) => {
    setEditing(target)
    setForm({
      title: target.title || "",
      content: target.content || "",
      active: Boolean(target.active),
      publishedAt: isoToDateInput(target.publishedAt || target.createdAt),
      bumpRevision: false,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error("공지 제목을 입력해주세요.")
      return
    }
    if (!form.content.trim()) {
      toast.error("공지 내용을 입력해주세요.")
      return
    }
    if (!auth.user?.userId) {
      toast.error("로그인 정보를 확인할 수 없습니다.")
      return
    }

    setSaving(true)
    try {
      const input: AnnouncementInput = {
        title: form.title.trim(),
        content: form.content.trim(),
        active: form.active,
        publishedAt: dateInputToIso(form.publishedAt),
      }
      if (editing) {
        await updateAnnouncement(editing, input, form.bumpRevision)
        toast.success(
          form.bumpRevision
            ? "공지를 수정했습니다. 모든 사용자에게 다시 표시됩니다."
            : "공지를 수정했습니다."
        )
      } else {
        await createAnnouncement(input, auth.user.userId, auth.userName)
        toast.success("공지를 등록했습니다.")
      }
      await fetchAnnouncements()
      setModalOpen(false)
      setEditing(null)
      setForm(EMPTY_FORM)
    } catch (error) {
      console.error("공지 저장 실패:", error)
      toast.error("공지 저장에 실패했습니다. 잠시 후 다시 시도해주세요.")
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (target: Announcement) => {
    try {
      const next = await toggleActive(target)
      await fetchAnnouncements()
      toast.success(next ? "공지를 활성화했습니다." : "공지를 비활성화했습니다.")
    } catch (error) {
      console.error("활성 상태 변경 실패:", error)
      toast.error("활성 상태 변경에 실패했습니다.")
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteAnnouncement(deleteTarget._id)
      await fetchAnnouncements()
      toast.success("공지를 삭제했습니다.")
    } catch (error) {
      console.error("공지 삭제 실패:", error)
      toast.error("공지 삭제에 실패했습니다.")
    } finally {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* 헤더 */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">공지사항 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            활성 공지 중 가장 최근 1건이 로그인 사용자 전체에게 팝업으로 표시됩니다.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-600"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          공지 작성
        </button>
      </div>

      {/* 현재 노출 중인 공지 */}
      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
            노출 중
          </span>
          {activeAnnouncement ? (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{activeAnnouncement.title}</p>
              <p className="mt-0.5 text-xs text-gray-600">
                게시일 {formatAnnouncementDate(activeAnnouncement.publishedAt || activeAnnouncement.createdAt)} · 리비전 v
                {activeAnnouncement.revision || 1}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">현재 활성화된 공지가 없어 팝업이 표시되지 않습니다.</p>
          )}
        </div>
      </div>

      {/* 목록 */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-amber-200 border-t-amber-500" />
            <p className="text-sm text-gray-500">공지 목록을 불러오는 중...</p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-gray-700">등록된 공지가 없습니다.</p>
            <p className="mt-1 text-xs text-gray-500">
              프로그램 업데이트 내용을 작성하면 사용자에게 팝업으로 안내됩니다.
            </p>
          </div>
        ) : (
          <>
            {/* 데스크톱 테이블 */}
            <table className="hidden w-full lg:table">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">제목</th>
                  <th className="w-28 px-4 py-3 text-left text-xs font-semibold text-gray-600">게시일</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-semibold text-gray-600">리비전</th>
                  <th className="w-28 px-4 py-3 text-left text-xs font-semibold text-gray-600">작성자</th>
                  <th className="w-24 px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                  <th className="w-48 px-4 py-3 text-right text-xs font-semibold text-gray-600">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {announcements.map((item) => (
                  <tr key={item._id} className={item.active ? "bg-amber-50/40" : ""}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatAnnouncementDate(item.publishedAt || item.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">v{item.revision || 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.creatorName || "-"}</td>
                    <td className="px-4 py-3">
                      {item.active ? (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                          활성
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          비활성
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleToggleActive(item)}
                          className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          {item.active ? "비활성화" : "활성화"}
                        </button>
                        <button
                          onClick={() => openEdit(item)}
                          className="rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item)}
                          className="rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 모바일 카드 */}
            <div className="divide-y divide-gray-100 lg:hidden">
              {announcements.map((item) => (
                <div key={item._id} className={`p-4 ${item.active ? "bg-amber-50/40" : ""}`}>
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    {item.active ? (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        활성
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        비활성
                      </span>
                    )}
                  </div>
                  <p className="mb-3 text-xs text-gray-500">
                    {formatAnnouncementDate(item.publishedAt || item.createdAt)} · v{item.revision || 1} ·{" "}
                    {item.creatorName || "-"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => handleToggleActive(item)}
                      className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700"
                    >
                      {item.active ? "비활성화" : "활성화"}
                    </button>
                    <button
                      onClick={() => openEdit(item)}
                      className="rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-medium text-amber-700"
                    >
                      수정
                    </button>
                    <button
                      onClick={() => setDeleteTarget(item)}
                      className="rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 작성/수정 모달 */}
      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div
            className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">{editing ? "공지 수정" : "공지 작성"}</h2>
              <button
                onClick={closeModal}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white/70 hover:text-gray-600"
                aria-label="닫기"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="max-h-[65vh] space-y-4 overflow-y-auto px-6 py-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="예) 일정관리 권한 개선 안내"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  내용 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                  rows={9}
                  placeholder={"업데이트된 내용을 입력해주세요.\n줄바꿈은 그대로 표시됩니다."}
                  className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">게시일</label>
                <input
                  type="date"
                  value={form.publishedAt}
                  onChange={(e) => setForm((prev) => ({ ...prev, publishedAt: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-100 sm:w-56"
                />
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3.5 transition-colors hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-amber-500"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">이 공지를 활성화</span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    활성화하면 기존 활성 공지는 자동으로 비활성 처리되어, 팝업은 항상 1개만 표시됩니다.
                  </span>
                </span>
              </label>

              {editing ? (
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 transition-colors hover:bg-amber-100/60">
                  <input
                    type="checkbox"
                    checked={form.bumpRevision}
                    onChange={(e) => setForm((prev) => ({ ...prev, bumpRevision: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 accent-amber-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">수정 내용을 다시 알림</span>
                    <span className="mt-0.5 block text-xs text-gray-600">
                      체크하면 리비전이 v{Number(editing.revision || 1) + 1}로 올라가, 이미 &lsquo;다시 보지 않기&rsquo;를 누른
                      사용자에게도 팝업이 다시 표시됩니다.
                    </span>
                  </span>
                </label>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <button
                onClick={closeModal}
                disabled={saving}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-600 disabled:opacity-50"
              >
                {saving ? "저장 중..." : editing ? "수정 저장" : "공지 등록"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* 삭제 확인 */}
      {deleteTarget ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 text-base font-bold text-gray-900">공지를 삭제하시겠습니까?</h3>
            <p className="mb-5 text-sm text-gray-600">
              &lsquo;{deleteTarget.title}&rsquo; 공지가 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
